import {UnitPriceChoice} from "@/components/docs/UnitPriceChoice";
/**
 * Quotation line grid.
 *
 * Deliberately NOT the shared <LineItemsEditor>: quotation_items carries seven
 * columns the PI grid has no use for (sub ref, MOQ/MCQ/MSQ, sample & bulk lead
 * time, image) and the legacy form only ever creates a line by picking a
 * catalog product — so the line here also carries productId, which the free-text
 * shared grid leaves NULL on every row it writes.
 */
import { useState } from "react";
import { QuotationProductImage } from "./QuotationProductImage";
import { Plus, Search, Trash2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ProductSummaryDto, QuotationLineDto } from "@hisense/shared";
import { describe, isFallback, withDescription, type DescriptionLang } from "@hisense/shared";
import { Button } from "@/components/ui/Button";
import { unitPriceToInput } from "@/domain/money";
import { parseIntField, parseMoneyMajor } from "@/lib/moneyInput";
import { useProducts } from "@/api/hooks";
import { useActiveUnitProducts } from "@/api/unitPricing";
import {
  QUOTATION_COLUMN_WIDTH, QUOTATION_DESCRIPTION_MIN_REM, QUOTATION_EDGE_COLUMNS_REM, QUOTATION_MAIN_COLUMNS, QUOTATION_SIDE_COLUMNS,
  type QuotationColumn,
} from "./quotationFormLayout";

export interface QuotationLineDraft {
  id?:string;
  unitSelection?:import("@hisense/shared").UnitSelection;
  productId: string;
  productCode: string;
  subRef: string;
  description: string;
  /** Bản tiếng Hoa, chụp cùng lúc với bản tiếng Anh. */
  descriptionZh: string;
  color: string;
  size: string;
  unit: string;
  moq: string;
  mcq: string;
  msq: string;
  sampleLeadTime: string;
  bulkLeadTime: string;
  imageUrl: string;
  remark: string;
  quantity: string;
  unitPrice: string; // major units, as typed
  /**
   * Ba mức giá chào, nhập theo đơn vị lớn như đã gõ.
   *
   * Lưu trên CHÍNH báo giá chứ không tra danh mục lúc in: báo giá là cam kết
   * với khách, đổi giá một sản phẩm không được sửa lại báo giá đã gửi.
   */
  dtdPrice: string;
  exwPrice: string;
  underMoqPrice: string;
}

export function blankQuotationLine(unit = ""): QuotationLineDraft {
  return {
    productId: "",
    productCode: "",
    subRef: "",
    description: "",
    descriptionZh: "",
    color: "",
    size: "",
    unit,
    moq: "",
    mcq: "",
    msq: "",
    sampleLeadTime: "",
    bulkLeadTime: "",
    imageUrl: "",
    remark: "",
    quantity: "1",
    unitPrice: "",
    dtdPrice: "",
    exwPrice: "",
    underMoqPrice: "",
  };
}

/** Prefill a line from a catalog product, the way the legacy picker did. */
export function lineFromProduct(
  p: ProductSummaryDto,
  currency = "USD",
): QuotationLineDraft {
  return {
    productId: p.id,
    productCode: p.productCode,
    subRef: p.subRef ?? "",
    description: p.description ?? "", descriptionZh: p.descriptionZh ?? "",
    color: p.color ?? "",
    size: p.size ?? "",
    unit: p.unit ?? "",
    moq: p.moq == null ? "" : String(p.moq),
    mcq: p.mcq == null ? "" : String(p.mcq),
    msq: p.msq == null ? "" : String(p.msq),
    sampleLeadTime: p.sampleLeadTime ?? "",
    bulkLeadTime: p.bulkLeadTime ?? "",
    imageUrl: p.imageUrl ?? "",
    // Ghi chú sản phẩm điền sẵn vào dòng, như hệ cũ: nó thường là dặn dò về
    // chính mặt hàng đó ("ép nhiệt 5 giây", "không giặt tẩy"), và bắt gõ lại
    // là mời người ta quên.
    remark: p.remarks ?? "",
    // Legacy hard-codes 1: a quotation prices a unit, it does not order a batch.
    quantity: "1",
    unitPrice:
      p.currency !== currency || p.sellingPrice == null
        ? ""
        : unitPriceToInput(p.sellingPrice),
    // Chọn từ danh mục thì điền sẵn giá hiện tại — nhưng đây là điểm KHỞI ĐẦU,
    // người lập vẫn sửa được, và giá đã sửa mới là thứ được lưu.
    dtdPrice: "",
    exwPrice: "",
    underMoqPrice: "",
  };
}

export function lineFromDto(it: QuotationLineDto): QuotationLineDraft {
  return {
    id:it.id,
    productId: it.productId ?? "",
    productCode: it.productCode ?? "",
    subRef: it.subRef ?? "",
    description: it.description ?? "",
    descriptionZh: it.descriptionZh ?? "",
    color: it.color ?? "",
    size: it.size ?? "",
    unit: it.unit ?? "",
    moq: it.moq == null ? "" : String(it.moq),
    mcq: it.mcq == null ? "" : String(it.mcq),
    msq: it.msq == null ? "" : String(it.msq),
    sampleLeadTime: it.sampleLeadTime ?? "",
    bulkLeadTime: it.bulkLeadTime ?? "",
    imageUrl: it.imageUrl ?? "",
    remark: it.remark ?? "",
    quantity: String(it.quantity),
    unitPrice: unitPriceToInput(it.unitPrice),
    dtdPrice: it.dtdPrice == null ? "" : unitPriceToInput(it.dtdPrice),
    exwPrice: it.exwPrice == null ? "" : unitPriceToInput(it.exwPrice),
    underMoqPrice:
      it.underMoqPrice == null ? "" : unitPriceToInput(it.underMoqPrice),
  };
}

type LineField =
  | "quantity"
  | "unitPrice"
  | "moq"
  | "mcq"
  | "msq"
  | "dtdPrice"
  | "exwPrice"
  | "underMoqPrice";

export interface QuotationLineInputPayload {
  descriptionZh?: string;
  id?:string;
  unitSelection?:import("@hisense/shared").UnitSelection;
  productId?: string;
  productCode?: string;
  subRef?: string;
  description?: string;
  color?: string;
  size?: string;
  unit?: string;
  moq?: number;
  mcq?: number;
  msq?: number;
  sampleLeadTime?: string;
  bulkLeadTime?: string;
  imageUrl?: string;
  remark?: string;
  quantity: 1;
  unitPrice: number;
  dtdPrice?: number;
  exwPrice?: number;
  underMoqPrice?: number;
}

/**
 * Drafts → API line inputs (minor units). Every numeric field is REJECTED when
 * it is not a clean number — never coerced to 0, which would silently ship a
 * wrong price.
 */
export function quotationDraftsToInput(
  lines: QuotationLineDraft[],
):
  | { items: QuotationLineInputPayload[]; total: number }
  | { error: { line: number; field: LineField } } {
  const items: QuotationLineInputPayload[] = [];
  let total = 0;
  for (const [i, l] of lines.entries()) {
    if (!l.description.trim() && !l.productCode.trim()) continue; // skip blank rows
    const at = (field: LineField) =>
      ({ error: { line: i + 1, field } }) as const;

    const price = parseMoneyMajor(l.unitPrice);
    if (price.invalid || price.minor == null) return at("unitPrice");
    const moq = parseIntField(l.moq);
    if (moq.invalid) return at("moq");
    const mcq = parseIntField(l.mcq);
    if (mcq.invalid) return at("mcq");
    const msq = parseIntField(l.msq);
    if (msq.invalid) return at("msq");
    // Ba giá chào cũng phải TỪ CHỐI khi gõ sai, không ép về 0 — một mức giá
    // sai bằng 0 gửi cho khách còn tệ hơn một thông báo lỗi.
    const dtd = parseMoneyMajor(l.dtdPrice);
    if (dtd.invalid) return at("dtdPrice");
    const exw = parseMoneyMajor(l.exwPrice);
    if (exw.invalid) return at("exwPrice");
    const underMoq = parseMoneyMajor(l.underMoqPrice);
    if (underMoq.invalid) return at("underMoqPrice");

    const quantity = 1;
    const unitPrice = price.minor;
    total += quantity * unitPrice;
    items.push({
      id:l.id,unitSelection:l.unitSelection,
      dtdPrice: dtd.minor,
      exwPrice: exw.minor,
      underMoqPrice: underMoq.minor,
      productId: l.productId || undefined,
      productCode: l.productCode || undefined,
      subRef: l.subRef || undefined,
      description: l.description || undefined,
      descriptionZh: l.descriptionZh || undefined,
      color: l.color || undefined,
      size: l.size || undefined,
      unit: l.unit || undefined,
      moq: moq.value,
      mcq: mcq.value,
      msq: msq.value,
      sampleLeadTime: l.sampleLeadTime || undefined,
      bulkLeadTime: l.bulkLeadTime || undefined,
      imageUrl: l.imageUrl || undefined,
      remark: l.remark || undefined,
      quantity,
      unitPrice,
    });
  }
  return { items, total };
}

const inputCls =
  "h-8 w-full rounded-xs border border-line bg-surface px-1.5 text-xs text-ink outline-none focus:border-brand";

/** Catalog picker preserves variant identity and exposes all result pages. */
function ProductPicker({
  onPick,
  disabledIds,
  currency,
}: {
  onPick: (p: ProductSummaryDto) => void;
  disabledIds: Set<string>;
  currency: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const results = useProducts({ q: q.trim() || undefined, page, pageSize: 20 });
  if (!open)
    return (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => setOpen(true)}
      >
        <Plus className="size-3.5" />
        {t("quotations.addProduct")}
      </Button>
    );
  const pages = Math.max(1, Math.ceil((results.data?.total ?? 0) / 20));
  return (
    <div className="w-full max-w-3xl">
      <div className="flex items-center gap-2">
        <div className="flex h-8 min-w-0 flex-1 items-center gap-1.5 rounded-xs border border-line-strong bg-surface px-2">
          <Search
            className="size-3.5 shrink-0 text-ink-faint"
            aria-hidden="true"
          />
          <input
            autoFocus
            aria-label={t("quotationWorkspace.productSearch")}
            className="min-w-0 flex-1 bg-transparent text-xs text-ink outline-none"
            placeholder={t("common.search")}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setQ("");
            setPage(1);
          }}
          aria-label={t("common.cancel")}
          className="text-ink-faint hover:text-ink"
        >
          <X className="size-4" />
        </button>
      </div>
      <ul
        className="mt-1 max-h-80 overflow-y-auto rounded-xs border border-line bg-surface"
        aria-busy={results.isFetching}
      >
        {results.isFetching ? (
          <li className="p-3 text-xs text-ink-faint" role="status">
            {t("common.loading")}
          </li>
        ) : results.isError ? (
          <li className="p-3 text-xs text-danger" role="alert">
            {t("quotationWorkspace.productLoadError")}{" "}
            <button
              type="button"
              className="underline"
              onClick={() => void results.refetch()}
            >
              {t("quotationWorkspace.retry")}
            </button>
          </li>
        ) : !results.data?.data.length ? (
          <li className="p-3 text-xs text-ink-faint">
            {t("quotationWorkspace.noProducts")}
          </li>
        ) : (
          results.data.data.map((p) => (
            <li
              key={p.id}
              className="flex items-start gap-3 border-b border-line px-3 py-3 last:border-b-0"
            >
              <QuotationProductImage
                product={p}
                selected={disabledIds.has(p.id)}
                onSelect={() => {
                  if (!disabledIds.has(p.id)) onPick(p);
                }}
              />
              <div className="min-w-0 flex-1 text-xs">
                <span className="min-w-0 flex-1">
                  <span className="block font-mono tabular text-ink">
                    {p.productCode}
                    {p.subRef ? ` · ${p.subRef}` : ""}
                  </span>
                  <span className="block whitespace-normal text-ink-soft">
                    {p.description}
                  </span>
                  <span className="mt-1 block text-ink-soft">
                    {t("common.color")}: {p.color || "—"} · {t("common.size")}:{" "}
                    {p.size || "—"} · {t("common.unit")}: {p.unit || "—"}
                  </span>
                  <span className="block text-2xs text-ink-faint">
                    {[p.customerRef, p.brandName, p.type]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  {p.currency !== currency && (
                    <span className="mt-1 block text-warn">
                      {t("quotationWorkspace.productCurrencyMismatch", {
                        currency: p.currency,
                      })}
                    </span>
                  )}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="mt-2"
                  disabled={disabledIds.has(p.id)}
                  aria-label={t("quotationWorkspace.selectProductRef", {
                    ref: [p.productCode, p.subRef].filter(Boolean).join(" / "),
                  })}
                  onClick={() => {
                    if (!disabledIds.has(p.id)) onPick(p);
                  }}
                >
                  {t(
                    disabledIds.has(p.id)
                      ? "quotationWorkspace.productSelected"
                      : "quotationWorkspace.selectProduct",
                  )}
                </Button>
              </div>
            </li>
          ))
        )}
      </ul>
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-ink-soft">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={page <= 1 || results.isFetching}
          onClick={() => setPage((n) => n - 1)}
        >
          {t("quotationWorkspace.previous")}
        </Button>
        <span>{t("quotationWorkspace.pageOf", { page, pages })}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={page >= pages || results.isFetching}
          onClick={() => setPage((n) => n + 1)}
        >
          {t("quotationWorkspace.next")}
        </Button>
      </div>
    </div>
  );
}

/** Nhãn cột (khoá i18n). Cột mô tả ghi thêm ngôn ngữ in đang sửa. */
const COLUMN_LABEL: Record<QuotationColumn, string> = {
  image: "common.image", productCode: "common.code", subRef: "common.subRef", description: "common.description",
  color: "common.color", size: "common.size", unit: "common.unit", unitPrice: "common.unitPrice", exwPrice: "common.exwPrice",
  underMoqPrice: "common.underMoqPrice", moq: "common.moq", sampleLeadTime: "common.sampleLeadTime",
  bulkLeadTime: "common.bulkLeadTime", mcq: "common.mcq", msq: "common.msq", dtdPrice: "common.dtdPrice", remark: "common.lineRemark",
};
const PRICE_COLUMNS = new Set<QuotationColumn>(["unitPrice", "exwPrice", "dtdPrice", "underMoqPrice"]);
const INTEGER_COLUMNS = new Set<QuotationColumn>(["moq", "mcq", "msq"]);
/** Đổi đơn vị thì giá cũ không còn đúng: xoá cả bốn giá để người lập gõ lại. */
const CLEARED_PRICES = { unitPrice: "", exwPrice: "", dtdPrice: "", underMoqPrice: "" } as const;
/** Cột mã dính trái khi bảng cuộn ngang; nút xoá dòng dính phải. */
const pinLeft = "sticky left-0 z-10 bg-surface";
const pinRight = "sticky right-0 z-10 bg-surface";

export function QuotationLinesEditor({
  lines,
  onChange,
  currency = "USD",
  units = [],
  readOnly = false,
  lang = "en",
  showSide = true,
}: {
  lines: QuotationLineDraft[];
  onChange: (lines: QuotationLineDraft[]) => void;
  currency?: string;
  units?: string[];
  readOnly?: boolean;
  /** Ngôn ngữ in của chính báo giá này — ô mô tả sửa đúng bản đó. */
  lang?: DescriptionLang;
  /** Hiện cột phụ (MCQ, MSQ, giá DTD, ghi chú dòng). Trang tính: có dòng dùng thì luôn hiện, không thì theo nút "Hiện cột phụ". */
  showSide?: boolean;
}) {
  const { t } = useTranslation();
  const set = (i: number, patch: Partial<QuotationLineDraft>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const pickedIds = new Set(lines.map((l) => l.productId).filter(Boolean));
  // Nút "Đơn vị & giá" chỉ ở dòng mà mã có hồ sơ quy đổi đã duyệt (19/09/2026 — chốt UOM-R01..R03).
  const activeUnits = useActiveUnitProducts(lines.map((l) => l.productId));
  const columns: QuotationColumn[] = [...QUOTATION_MAIN_COLUMNS, ...(showSide ? QUOTATION_SIDE_COLUMNS : [])];
  /*
   * Độ rộng CỐ ĐỊNH từng cột (table-fixed, 19/09/2026 — cùng chuẩn bảng PI), cột mô tả lấy phần còn lại. Trước đây bảng 16 ô
   * tự giãn tới ~3.000 px nên ở khổ 1600 px chỉ thấy tới cột Đơn vị: muốn gõ giá phải cuộn ngang.
   */
  const minRem = (readOnly ? 2 : QUOTATION_EDGE_COLUMNS_REM) +
    columns.reduce((sum, c) => sum + (QUOTATION_COLUMN_WIDTH[c] ?? QUOTATION_DESCRIPTION_MIN_REM), 0);
  const label = (c: QuotationColumn) =>
    c === "description" ? `${t(COLUMN_LABEL[c])} · ${t(`common.printLanguage_${lang}`)}` : t(COLUMN_LABEL[c]);

  const cell = (line: QuotationLineDraft, i: number, c: QuotationColumn) => {
    const aria = t("quotationWorkspace.lineField", { line: i + 1, field: t(COLUMN_LABEL[c]) });
    const quiet = "disabled:border-transparent disabled:bg-transparent disabled:opacity-100";
    if (c === "image") {
      return (
        <QuotationProductImage
          product={{ productCode: line.productCode, subRef: line.subRef, description: describe(line, lang), color: line.color, size: line.size, imageUrl: line.imageUrl }}
          compact
        />
      );
    }
    if (c === "description") {
      const value = lang === "zh" ? line.descriptionZh : line.description;
      return (
        <input aria-label={aria} disabled={readOnly} className={`${inputCls} ${quiet}`} value={value} title={value || undefined}
          // Bản của ngôn ngữ in đang trống thì bản in lùi về bản còn lại (describe): hiện mờ đúng chữ sẽ được in.
          placeholder={isFallback(line, lang) ? describe(line, lang) : undefined}
          onChange={(e) => set(i, withDescription(line, lang, e.target.value))} />
      );
    }
    const isPrice = PRICE_COLUMNS.has(c);
    const isInteger = INTEGER_COLUMNS.has(c);
    const key = c as Exclude<QuotationColumn, "image" | "description">;
    const value = line[key];
    const invalid = isPrice ? parseMoneyMajor(value).invalid : isInteger ? parseIntField(value).invalid : false;
    const input = (
      <input
        aria-label={aria}
        disabled={readOnly}
        required={c === "unitPrice"}
        className={`${inputCls} ${isPrice || isInteger ? "text-right font-mono tabular" : c === "productCode" || c === "subRef" ? "font-mono" : ""} ${quiet}`}
        inputMode={isPrice ? "decimal" : isInteger ? "numeric" : undefined}
        aria-invalid={invalid || undefined}
        list={c === "unit" ? "quotation-units" : undefined}
        value={value}
        title={!isPrice && !isInteger && value ? value : undefined}
        onChange={(e) =>
          set(i, {
            [key]: e.target.value,
            ...(c === "unit" ? CLEARED_PRICES : {}),
            // Sửa mã hay mã phụ là thành một mặt hàng khác: bỏ liên kết danh mục.
            ...(c === "productCode" || c === "subRef" ? { productId: "" } : {}),
          })
        }
      />
    );
    if (c === "productCode" && line.productId && !readOnly && activeUnits.has(line.productId)) {
      return (
        <>
          {input}
          <UnitPriceChoice key={`${line.productId}:${currency}`} productId={line.productId} currency={currency} unit={line.unit} quantity="1"
            unitPrice={line.unitPrice} unitQuotation onApply={(choice) => set(i, { ...choice, exwPrice: "", dtdPrice: "", underMoqPrice: "" })} />
        </>
      );
    }
    return input;
  };

  return (
    <div className="bg-surface">
      {lines.some((line) => line.quantity !== "1") && (
        <p className="border-b border-line bg-warn-tint px-4 py-2 text-xs text-warn" role="status">
          {t("quotationWorkspace.legacyQuantityNotice")}
        </p>
      )}
      <div className="overflow-x-auto" tabIndex={0}>
        <table className="w-full table-fixed border-collapse text-sm" style={{ minWidth: `${minRem}rem` }}>
          <colgroup>
            <col style={{ width: "2rem" }} />
            {columns.map((c) => {
              const w = QUOTATION_COLUMN_WIDTH[c];
              return <col key={c} style={w ? { width: `${w}rem` } : undefined} />;
            })}
            {!readOnly && <col style={{ width: "2rem" }} />}
          </colgroup>
          <thead>
            <tr className="[&>th]:border-b [&>th]:border-line [&>th]:bg-sunken/60 [&>th]:px-1.5 [&>th]:py-2 [&>th]:align-bottom [&>th]:text-2xs [&>th]:font-semibold [&>th]:uppercase [&>th]:leading-tight [&>th]:tracking-wide [&>th]:text-ink-faint">
              <th className="text-right">#</th>
              {columns.map((c) => (
                <th key={c} className={`${PRICE_COLUMNS.has(c) || INTEGER_COLUMNS.has(c) ? "text-right" : "text-left"} ${c === "productCode" ? `${pinLeft} bg-sunken` : ""}`}>
                  {label(c)}
                </th>
              ))}
              {!readOnly && <th className={`${pinRight} bg-sunken`}><span className="sr-only">{t("common.removeLine")}</span></th>}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr>
                <td colSpan={columns.length + (readOnly ? 1 : 2)} className="px-4 py-6 text-center text-xs text-ink-faint">
                  {t("quotationWorkspace.noLines")}
                </td>
              </tr>
            )}
            {lines.map((line, i) => (
              <tr key={i} className="[&>td]:border-b [&>td]:border-line [&>td]:px-1.5 [&>td]:py-1.5 [&>td]:align-top">
                <td className="pt-3.5 text-right font-mono tabular text-2xs text-ink-faint">{String(i + 1).padStart(2, "0")}</td>
                {columns.map((c) => (
                  <td key={c} className={c === "productCode" ? pinLeft : undefined}>{cell(line, i, c)}</td>
                ))}
                {!readOnly && (
                  <td className={`pt-3 text-center ${pinRight}`}>
                    <button
                      type="button"
                      onClick={() => onChange(lines.filter((_, j) => j !== i))}
                      aria-label={t("quotationWorkspace.lineField", { line: i + 1, field: t("common.removeLine") })}
                      className="text-ink-faint hover:text-danger"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <datalist id="quotation-units">
        {units.map((unit) => (
          <option key={unit} value={unit} />
        ))}
      </datalist>
      {!readOnly && (
        <div className="flex flex-wrap items-start gap-2 px-3 py-2">
          <ProductPicker
            onPick={(p) => onChange([...lines, lineFromProduct(p, currency)])}
            disabledIds={pickedIds}
            currency={currency}
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange([...lines, blankQuotationLine(units[0] ?? "")])}>
            <Plus className="size-3.5" />
            {t("common.addLine")}
          </Button>
        </div>
      )}
    </div>
  );
}
